import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Target } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { MobileHeader } from '../components/MobileHeader';
import NowCard, { KIND } from '../components/cockpit/NowCard';
import ChoosePanel from '../components/cockpit/ChoosePanel';
import AddButton from '../components/cockpit/AddButton';

const keyStore = (key) => ({
  get: () => { try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; } },
  set: (v) => { try { v ? sessionStorage.setItem(key, JSON.stringify(v)) : sessionStorage.removeItem(key); } catch { /* navigation privée */ } },
});
const store = keyStore('cockpit-session');
// Cartes traitées pendant la session, pour « ← Précédente » : [{ card, action, session }]
const historyStore = keyStore('cockpit-history');

const fmtHour = (iso) => { try { return format(new Date(iso), 'HH:mm'); } catch { return ''; } };
const hoursSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 3.6e6 : Infinity);

function MyDay() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const d = format(new Date(), 'yyyy-MM-dd');
    const q = `start=${d}T00:00:00&end=${d}T23:59:59`;
    Promise.all([api.get(`/classes?${q}`).catch(() => []), api.get(`/personal-events?${q}`).catch(() => [])])
      .then(([classes, events]) => setItems([...classes.map(c => ({ at: c.datetime_start, title: c.title || (c.type === 'group' ? 'Cours collectif' : 'Cours privé') })),
        ...events.map(e => ({ at: e.datetime_start, title: e.title }))].sort((a, b) => (a.at || '').localeCompare(b.at || ''))));
  }, []);
  return (
    <Box title="Ma journée">
      {items === null ? <span className="text-slate-400">…</span>
        : items.length === 0 ? <span className="text-slate-400">Rien dans l'agenda</span>
          : items.map((x, i) => <Line key={i} left={fmtHour(x.at)} right={x.title} />)}
    </Box>
  );
}

const Box = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-1.5 text-sm">
    <h3 className="font-extrabold text-slate-900 font-manrope mb-1">{title}</h3>{children}
  </div>
);
const Line = ({ left, right }) => <div className="flex justify-between gap-3"><span className="text-slate-500">{left}</span><span className="text-slate-800 text-right">{right}</span></div>;

export default function Today() {
  const [session, setSession] = useState(() => (store.get()?.place === 'partout' ? store.get() : null));
  const [today, setToday] = useState(null);
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(() => (historyStore.get() || []).filter(h => h.session === store.get()?.id));
  const [viewIdx, setViewIdx] = useState(null);  // index dans history quand on regarde une carte précédente
  const saveHistory = (h) => { historyStore.set(h); setHistory(h); };

  // v2.10 : plus de questions au démarrage — une session « partout » se crée toute seule.
  const newSession = useCallback(async () => {
    const s = await api.post('/cockpit/sessions', {});
    store.set(s); saveHistory([]); setSession(s);
  }, []);
  useEffect(() => { if (!session) newSession().catch(e => toast.error(e.message)); }, [session, newSession]);

  const load = useCallback(async (s = session) => {
    if (!s) return;
    try { setToday(await api.get(`/cockpit/today?session=${s.id}`)); }
    catch (e) { if (e.status === 404) { store.set(null); setSession(null); } else toast.error(e.message); }
  }, [session]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); setChoice(null); await load(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const card = today?.now;
  const post = (path, body) => api.post(`/cockpit/cards/${card.id}/${path}`, body);
  // Terminé / Plus tard / Changer / Envoyer : la carte suivante s'affiche tout de suite, le serveur suit en arrière-plan.
  const actOnCard = (action, request) => {
    saveHistory([...history, { card, action, session: session.id }]);
    setChoice(null);
    setToday(t => ({ ...t, now: t.next, next: null }));
    request().catch(e => toast.error(e.message)).finally(() => load());
  };
  // Note, case cochée, brouillon : la carte reste affichée, mise à jour.
  const update = (request) => request().then(c => setToday(t => (t?.now?.id === c.id ? { ...t, now: c } : t)))
    .catch(e => toast.error(e.message));
  const prev = viewIdx !== null ? history[viewIdx] : null;
  const undo = () => act(async () => {
    await api.post(`/cockpit/cards/${prev.card.id}/undo?session=${session.id}`);
    saveHistory(history.filter((_, i) => i !== viewIdx));
    setViewIdx(null);
  });
  const remove = () => act(async () => { await post('delete'); });

  const outbox = today?.outbox || [];
  return (
    <div className="max-w-5xl mx-auto pb-28 lg:pb-6" data-testid="today-page">
      <MobileHeader title="Aujourd'hui" />
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_260px] lg:gap-5 space-y-3 lg:space-y-0">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">{today ? `${today.stats.today} faite(s) aujourd'hui` : ''}</span>
          </div>
          {hoursSince(today?.last_claude_pass) > 26 && (
            <div className="rounded-xl bg-slate-100 text-slate-500 text-xs px-3 py-2">
              File pas mise à jour depuis {today?.last_claude_pass ? format(new Date(today.last_claude_pass), "EEEE d MMM 'à' HH:mm", { locale: fr }) : 'un moment'}.
            </div>
          )}
          {outbox.length > 0 && (
            <div className="rounded-xl bg-[#0288D1]/10 text-[#01579B] text-sm px-4 py-2.5" data-testid="outbox">
              📤 En cours d'envoi par Claude : {outbox.map(o => `${o.contact || '?'} (${o.channel || '?'})`).join(', ')}
            </div>
          )}
          <button type="button" data-testid="btn-choose" disabled={busy}
            onClick={() => api.get(`/cockpit/choose?session=${session.id}`).then(setChoice).catch(e => toast.error(e.message))}
            className="w-full flex items-center gap-2 bg-white rounded-2xl border border-slate-200 px-4 py-3 font-bold text-[#0288D1]">
            <Target size={18} /> Aide-moi à choisir
          </button>
          {choice && <ChoosePanel data={choice} session={session} onClose={() => setChoice(null)}
            onPick={(id) => act(() => api.post(`/cockpit/cards/${id}/pick?session=${session.id}`))} />}

          {history.length > 0 && (viewIdx === null || viewIdx > 0) && (
            <button type="button" data-testid="btn-previous"
              onClick={() => setViewIdx(i => (i === null ? history.length - 1 : i - 1))}
              className="text-sm font-semibold text-slate-500 hover:text-slate-700">← Précédente</button>
          )}
          {prev ? (
            <NowCard card={prev.card} previous={prev.action} busy={busy} onUndo={undo} onBack={() => setViewIdx(null)} />
          ) : today && (card ? (
            <NowCard card={card} busy={busy}
              onDone={() => actOnCard('done', () => post(`done?session=${session.id}`))}
              onNoReply={() => actOnCard('noreply', () => post(`done?session=${session.id}`))}
              onWait={(when, note) => actOnCard('wait', () => post('wait', { when, note }))}
              onFix={(text) => actOnCard('fix', () => post('fix', { text }))}
              onSend={(text) => actOnCard('send', () => post('send', { text }))}
              onNote={(text) => update(() => post('note', { text }))}
              onStep={(i) => update(() => post(`steps/${i}`))}
              onDraft={(text) => update(() => post('draft', { text }))}
              onDelete={remove} />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center text-slate-600" data-testid="empty-queue">
              Tout est fait pour l'instant 🎉 Tu peux noter une idée avec ＋.
            </div>
          ))}
          {today?.next && (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-500" data-testid="next-card">
              Ensuite : {KIND[today.next.kind]?.icon} {today.next.title} · ~{today.next.duration_min} min
            </div>
          )}
          {today && <div className="lg:hidden rounded-xl bg-[#26A69A]/10 text-[#00695c] text-sm px-4 py-2.5">
            ✓ Fait aujourd'hui : {today.stats.today} · semaine : {today.stats.week} · série : {today.stats.streak} jour(s)</div>}
        </div>

        <div className="hidden lg:block space-y-3">
          <MyDay />
          {today && (
            <Box title="La file du jour">
              {Object.entries(today.counts).map(([k, n]) => <Line key={k} left={KIND[k]?.label || k} right={n} />)}
              <p className="text-xs text-slate-400 pt-1">Ordre et découpage faits par Claude.</p>
            </Box>
          )}
          {today && <div className="rounded-2xl bg-[#26A69A]/10 text-[#00695c] text-sm px-4 py-3">
            ✓ {today.stats.today} aujourd'hui · {today.stats.week} cette semaine · série {today.stats.streak} j</div>}
        </div>
      </div>
      <AddButton />
    </div>
  );
}
